import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Heart, ArrowLeft } from 'lucide-react'
import { contentApi, type ContentListItem } from '../../lib/api'
import SectionBanner from '../../components/SectionBanner'
import ContentCard from '../../components/ContentCard'
import EmptyState from '../../components/EmptyState'
import { usePageTitle } from '../../hooks/usePageTitle'

const PAGE_SIZE = 20

const Favorites = () => {
  usePageTitle()
  const [items, setItems] = useState<(ContentListItem & { favorited_at?: string })[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const res = await contentApi.listFavorites({ page, page_size: PAGE_SIZE })
      setItems(res.items)
      setTotal(res.total)
    } catch (e: any) {
      setError(e.message || '加载失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [page])

  const onUnfavorite = async (id: string) => {
    try {
      await contentApi.removeFavorite(id)
      load()
    } catch (e) {
      console.error('Unfavorite failed', e)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      <SectionBanner title="我的收藏" subtitle="你感兴趣的健康科普都在这里">
        <Link to="/user/knowledge" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 mt-1">
          <ArrowLeft className="w-3 h-3" />返回科普列表
        </Link>
      </SectionBanner>

      <div className="px-4 -mt-6 relative z-10">
        {error ? (
          <EmptyState variant="error" description={error} />
        ) : loading ? (
          <EmptyState variant="loading" />
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md">
            <EmptyState
              title="还没有收藏的内容"
              description="去发现感兴趣的健康科普吧"
              action={{ label: '浏览科普', onClick: () => window.location.href = '/user/knowledge' }}
            />
          </div>
        ) : (
          <>
            <div className="text-xs text-gray-500 px-1 mb-3 flex items-center gap-2">
              <Heart className="w-3 h-3 text-red-500 fill-current" />
              <span>共 {total} 条收藏</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map(item => (
                <ContentCard
                  key={item.id}
                  item={item}
                  showFavorite
                  favorited
                  onFavorite={onUnfavorite}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 text-sm pt-4">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
                >上一页</button>
                <span className="text-gray-500">{page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-4 py-1.5 bg-white border rounded-full disabled:opacity-40 hover:bg-gray-50"
                >下一页</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Favorites
